
import { toast } from 'sonner';
import { AppData } from '@/utils/storage';
import { celebrateAchievement } from '@/utils/animations';

export const useAchievementManager = (
  data: AppData | null, 
  updateData: (newData: AppData) => Promise<AppData>
) => {
  const handleUnlockAchievement = (achievementId: string) => {
    if (!data) return;
    
    let wasUnlocked = false;
    let achievementTitle = '';
    
    const updatedAchievements = data.achievements.map(a => {
      if (a.id === achievementId && !a.unlocked) {
        // Track that we're unlocking an achievement
        wasUnlocked = true;
        achievementTitle = a.title;
        
        return {
          ...a,
          unlocked: true,
          date: new Date().toISOString()
        };
      }
      return a;
    });
    
    const newData = {
      ...data,
      achievements: updatedAchievements
    };

    updateData(newData).catch((error) => {
      console.error('[useAchievementManager] Failed to persist update:', error);
    });

    // Only celebrate if an achievement was actually unlocked
    if (wasUnlocked) {
      // Play celebration animation with trophy name
      celebrateAchievement(achievementTitle);
    }
  };

  const handleDeleteAchievement = (achievementId: string) => {
    if (!data) return;
    
    const updatedAchievements = data.achievements.filter(a => a.id !== achievementId);
    const newData = {
      ...data,
      achievements: updatedAchievements
    };

    updateData(newData).catch((error) => {
      console.error('[useAchievementManager] Failed to persist deletion:', error);
    });
  };

  const handleMoveAchievement = (achievementId: string, direction: 'up' | 'down') => {
    if (!data) return;
    
    const achievementIndex = data.achievements.findIndex(a => a.id === achievementId);
    if (achievementIndex === -1) return;
    
    // Don't move if already at the top/bottom
    if (direction === 'up' && achievementIndex === 0) return;
    if (direction === 'down' && achievementIndex === data.achievements.length - 1) return;
    
    const newAchievements = [...data.achievements];
    const swapIndex = direction === 'up' ? achievementIndex - 1 : achievementIndex + 1;
    
    // Swap the achievements
    [newAchievements[achievementIndex], newAchievements[swapIndex]] = [
      newAchievements[swapIndex], 
      newAchievements[achievementIndex]
    ];
    
    // Add order property if it doesn't exist
    const achievementsWithOrder = newAchievements.map((achievement, index) => ({
      ...achievement,
      order: index
    }));
    
    const newData = {
      ...data,
      achievements: achievementsWithOrder
    };

    updateData(newData).catch((error) => {
      console.error('[useAchievementManager] Failed to persist reorder:', error);
    });
  };

  return {
    handleUnlockAchievement,
    handleDeleteAchievement,
    handleMoveAchievement
  };
};
